// Feature: M004-F0713. Tests: T-M004-0205, T-M004-0206, T-M004-0207.
//
// The shared, merged Core + Ledger transaction timeline (design M004-F0712-F0716 sections 2.4a-2.7). Mounted by the
// history drawer (TxHistoryDrawer) and by the booking drawer's History tab, so there is ONE renderer.
//
//   <TxTimeline history={data} onRetry={fn} />
//
// `history` is the `data` of getBillingTransactionHistory (Core's body), or the `data` of getLedgerTransactionHistory
// (the Ledger-only fallback), or an already-normalised model (services/txHistoryAdapter). Rules it enforces:
//  - ACCT-TX-011: Core's `summary` is rendered byte-for-byte; a WebUI sentence appears only where the entry has none,
//    and then carries the muted "(no description supplied)" suffix.
//  - ACCT-TX-006: rows are rendered in the order DELIVERED, never re-sorted.
//  - A half that did not load says so (never "No events", never an empty list).
//  - No amount is shown unless `can.view_amounts.allowed`.
import React, { useMemo } from 'react';
import { asTxModel } from '../../services/txHistoryAdapter';
import {
  ATTENTION_ACTIONS, NO_DESCRIPTION_SUFFIX, PLAN_CHANGE_FIELD_LABELS,
  amountText, conditionBadge, eventSentence, eventTag,
} from './txStages';
import './TxTimeline.css';

const SKEW_MS = 5000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const validZone = (zone) => {
  if (typeof zone !== 'string' || !zone) return '';
  try { new Intl.DateTimeFormat('en-GB', { timeZone: zone }); return zone; } catch { return ''; }
};

// "8 Oct 2026, 20:05 BST": absolute, always with a zone abbreviation (UTC when no zone is known), never relative.
export function formatTxTime(at, timezone) {
  const instant = new Date(at);
  if (!at || Number.isNaN(instant.getTime())) return typeof at === 'string' ? at : '';
  const zone = validZone(timezone) || 'UTC';
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: zone, day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short',
    }).formatToParts(instant).map((part) => [part.type, part.value]));
    return `${Number(parts.day)} ${MONTHS[Number(parts.month) - 1]} ${parts.year}, ${parts.hour}:${parts.minute} ${parts.timeZoneName || zone}`;
  } catch {
    return instant.toISOString();
  }
}

const utcTitle = (at) => {
  const instant = new Date(at);
  return at && !Number.isNaN(instant.getTime()) ? instant.toISOString() : undefined;
};

export const actorText = (actor) => {
  if (!actor || typeof actor !== 'object') return '';
  if (actor.kind === 'system') return actor.purpose ? `System: ${actor.purpose}` : 'System';
  return typeof actor.display_name === 'string' && actor.display_name.trim() ? actor.display_name : 'A user';
};

// ---- Row view model ----
const gapBetween = (previous, next) => {
  if (!previous) return false;
  if (Number.isFinite(previous.seq) && Number.isFinite(next.seq) && next.seq > previous.seq + 1) return true;
  if (previous.to_stage && next.from_stage && previous.to_stage !== next.from_stage) return true;
  return false;
};

// Per-source gap check and the cross-system clock-skew check. Pure: the delivered order is the order of `rows`.
export function buildTimelineRows(model, timezone) {
  const zone = timezone || model.timezone;
  const canAmounts = model.canViewAmounts;
  const events = Array.isArray(model.timeline) ? model.timeline : [];
  const lastBySource = new Map();
  const rows = [];
  let skew = false;
  events.forEach((ev, index) => {
    const source = ev?.origin_system === 'ledger' ? 'ledger' : 'core';
    const previousSame = lastBySource.get(source);
    const tag = eventTag(ev);
    if (gapBetween(previousSame, ev)) {
      const previousTag = previousSame.__tag;
      rows.push({ key: `gap-${index}`, gap: true, text: `Something between ${previousTag} and ${tag} was not recorded here.` });
    }
    const after = canAmounts ? amountText(ev?.amount?.after_minor, ev?.amount?.currency) : '';
    const sentence = eventSentence(ev, { after, who: actorText(ev?.actor) === 'A user' ? '' : actorText(ev?.actor) });
    const before = canAmounts ? amountText(ev?.amount?.before_minor, ev?.amount?.currency) : '';
    const amount = !canAmounts || !ev?.amount ? '' : (before && after ? `${before} -> ${after}` : (after || before));
    const previousEvent = events[index - 1];
    if (previousEvent && previousEvent.origin_system !== ev?.origin_system) {
      const a = new Date(previousEvent.at).getTime();
      const b = new Date(ev.at).getTime();
      if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(b - a) < SKEW_MS) skew = true;
    }
    rows.push({
      key: ev?.event_id || `ev-${index}`,
      gap: false,
      ev,
      source,
      tag,
      sentence,
      at: ev?.at,
      time: formatTxTime(ev?.at, zone),
      who: actorText(ev?.actor),
      reason: typeof ev?.reason === 'string' && ev.reason !== '' && !sentence.reasonIsSentence ? ev.reason : '',
      amount,
      attention: ev?.kind === 'anomaly' || ATTENTION_ACTIONS.includes(ev?.action),
      synthesized: ev?.synthesized === true,
      overridden: Array.isArray(ev?.correlation?.overridden_conditions) ? ev.correlation.overridden_conditions : [],
      ledgerEntryId: ev?.correlation?.ledger_entry_id || '',
    });
    lastBySource.set(source, { seq: ev?.seq, to_stage: ev?.to_stage, __tag: tag });
  });
  return { rows, skew };
}

// ---- Cause facts (2.5): labels ours, values Core's, never recomposed into a sentence ----
const Fact = ({ label, value }) => (value === '' || value == null ? null : <li><span className="tx-fact-label">{label}</span> <span className="tx-fact-value">{String(value)}</span></li>);
const RATE_FIELDS = ['first_hour_rate', 'long_hour_rate'];

export function CauseFacts({ cause, zone, canAmounts }) {
  if (!cause || typeof cause !== 'object') return null;
  const when = (at) => formatTxTime(at, zone);
  const by = (actor) => actorText(actor);
  let facts = null;
  switch (cause.kind) {
    case 'plan_updated':
      facts = (
        <>
          <Fact label="Plan" value={`${cause.plan_name} (version ${cause.plan_version})`} />
          {(Array.isArray(cause.changes) ? cause.changes : []).map((change) => (
            <Fact
              key={change.field}
              label={PLAN_CHANGE_FIELD_LABELS[change.field] || change.field}
              value={!canAmounts && RATE_FIELDS.includes(change.field) ? 'Hidden for your access' : `${change.old_value ?? ''} -> ${change.new_value ?? ''}`}
            />
          ))}
          {cause.partial ? <li className="tx-fact-partial">Who made this change was not recorded.</li> : <Fact label="Changed by" value={by(cause.changed_by)} />}
          <Fact label="Changed" value={when(cause.changed_at)} />
        </>
      );
      break;
    case 'plan_assigned':
    case 'plan_unassigned':
      facts = (
        <>
          <Fact label="Plan" value={cause.plan_name} />
          <Fact label="For" value={cause.subject_user_id != null ? `user #${cause.subject_user_id}` : ''} />
          <Fact label="Effective from" value={cause.effective_from} />
          <Fact label="Effective to" value={cause.effective_to} />
          <Fact label="Changed by" value={by(cause.changed_by)} />
          <Fact label="Changed" value={when(cause.changed_at)} />
        </>
      );
      break;
    case 'default_changed':
      facts = (<><Fact label="Plan" value={cause.plan_name} /><Fact label="Changed by" value={by(cause.changed_by)} /><Fact label="Changed" value={when(cause.changed_at)} /></>);
      break;
    case 'admin_override':
      facts = (<><Fact label="Override" value={cause.override === 'named_plan' ? 'Plan chosen' : 'Custom price'} /><Fact label="Set by" value={by(cause.set_by)} /><Fact label="Reason" value={cause.reason} /></>);
      break;
    case 'price_text_changed':
      facts = (<><li>Calendar price text changed</li><Fact label="Before" value={cause.old_clause} /><Fact label="After" value={cause.new_clause} /></>);
      break;
    case 'booking_rescheduled':
      facts = (<><Fact label="Was" value={`${when(cause.old_start)} - ${when(cause.old_end)}`} /><Fact label="Now" value={`${when(cause.new_start)} - ${when(cause.new_end)}`} /></>);
      break;
    case 'system_recapture':
      facts = <Fact label="System" value={cause.purpose} />;
      break;
    case 'default_fallback':
      facts = (<><li>No plan applied; the default price was used</li><Fact label="Step" value={cause.step} /></>);
      break;
    default:
      return null;
  }
  return <ul className="tx-cause-facts" data-testid="tx-cause-facts">{facts}</ul>;
}

// ---- Ledger half notices (2.7): every reason has its own sentence; Core's `message` is shown verbatim under it ----
const LEDGER_NOTICES = Object.freeze({
  not_linked: { text: "This organisation's Ledger is not linked, so the Ledger's part of this history is not available.", retry: false },
  tunnel_down: { text: "Core could not reach the Ledger just now, so the Ledger's part of this history is missing here. The transaction itself is not changed.", retry: true },
  timeout: { text: "The Ledger did not answer in time, so its part of this history is missing here. The transaction itself is not changed.", retry: true },
  not_permitted: { text: "You can see Core's part of this history. The Ledger's part is shown to Ledger managers for this organisation.", retry: false },
  not_handed_off: { text: 'The Ledger has no record of this transaction yet, so it has nothing to show.', retry: false },
  unsupported: { text: "This organisation's Ledger is on an older version and cannot show its part of the history yet.", retry: false },
  refused: { text: "The Ledger refused to show its part of this history.", retry: false },
});
const UNKNOWN_LEDGER_NOTICE = Object.freeze({ text: "The Ledger's part of this history is not available.", retry: true });

export function LedgerHalfNotice({ ledger, onRetry }) {
  if (!ledger || ledger.available) return null;
  const notice = LEDGER_NOTICES[ledger.reason] || UNKNOWN_LEDGER_NOTICE;
  return (
    <div className="tx-notice" role="status" data-testid="tx-ledger-notice" data-reason={ledger.reason || 'unknown'}>
      <p>{notice.text}</p>
      {ledger.message ? <p className="tx-notice-message" data-testid="tx-ledger-message">{ledger.message}</p> : null}
      {notice.retry && onRetry ? <button type="button" className="tx-link-button" onClick={onRetry}>Try again</button> : null}
    </div>
  );
}

// ---- Linked documents (credit notes / corrections / TX-valued children), nested under the parent, never as a separate TX ----
const DOC_LABELS = Object.freeze({ credit_note: 'Credit note', correction: 'Correction' });
const CHILD_TX_LABELS = Object.freeze({ credit: 'Credit', receipt: 'Receipt', manual: 'Manual line' });

export function LinkedDocuments({ documents, canAmounts, zone, renderTxChip }) {
  const items = Array.isArray(documents) ? documents : [];
  if (!items.length) return null;
  const hasCorrection = items.some((child) => child.kind === 'correction');
  return (
    <section className="tx-linked" aria-label="Linked documents" data-testid="tx-linked-documents">
      <h4>Linked documents</h4>
      <ul>
        {items.map((child, index) => {
          const label = DOC_LABELS[child.kind] || CHILD_TX_LABELS[child.kind] || child.kind;
          const parts = [child.document_ref ? `${label} ${child.document_ref}` : label, formatTxTime(child.at, zone)];
          const amount = canAmounts ? amountText(child.amount_minor, undefined) : '';
          return (
            <li key={`${child.document_ref || child.hand_off_id || 'child'}-${index}`} data-kind={child.kind} data-testid="tx-linked-document">
              {parts.filter(Boolean).join(' . ')}
              {amount ? ` . ${amount}` : ''}
              {child.tx_id && renderTxChip ? <> {renderTxChip(child)}</> : null}
            </li>
          );
        })}
      </ul>
      {hasCorrection ? <p className="tx-linked-note">A correction is billed as its own line under this transaction. The original invoiced line is unchanged.</p> : null}
    </section>
  );
}

// ---- Invoice, ledger entry and payments (design 2.3 item 6): members that are absent are omitted, never dashes ----
export function TxInvoiceAndLedger({ history, timezone }) {
  const model = useMemo(() => asTxModel(history), [history]);
  if (!model) return null;
  const zone = timezone || model.timezone;
  if (!model.invoice && !model.ledgerEntryId && !model.payments.length) return null;
  return (
    <section className="tx-invoice" aria-label="Invoice and ledger" data-testid="tx-invoice-ledger">
      <h4>Invoice and ledger</h4>
      <ul>
        {model.invoice ? <li>{model.invoice.number ? `Invoice ${model.invoice.number}` : 'Invoice (not numbered yet)'}{` (${model.invoice.status})`}</li> : null}
        {model.ledgerEntryId ? <li data-testid="tx-ledger-entry">Ledger entry {model.ledgerEntryId}</li> : null}
        {model.payments.map((payment, index) => (
          <li key={`${payment.at}-${index}`}>Payment {formatTxTime(payment.at, zone)}{model.canViewAmounts ? ` . ${amountText(payment.amount_minor, payment.currency)}` : ''}</li>
        ))}
      </ul>
    </section>
  );
}

export default function TxTimeline({ history, timezone, onRetry, renderTxChip, showLinked = true }) {
  const model = useMemo(() => asTxModel(history), [history]);
  const view = useMemo(() => (model ? buildTimelineRows(model, timezone) : { rows: [], skew: false }), [model, timezone]);
  if (!model) return null;
  const zone = timezone || model.timezone;
  const halfFailed = !model.ledger.available && !model.ledgerOnly;
  // "No events recorded yet." only for a half that LOADED with zero entries (a failed half renders its notice instead).
  const loadedEmpty = view.rows.length === 0 && !halfFailed;
  return (
    <section className="tx-timeline" aria-label="Transaction timeline" data-testid="tx-timeline">
      <LedgerHalfNotice ledger={model.ledger} onRetry={onRetry} />
      {model.journalPartial ? <p className="tx-muted" data-testid="tx-journal-partial">Some older records were removed by retention, so the start of this history is incomplete.</p> : null}
      {model.journalTruncated ? <p className="tx-muted" data-testid="tx-journal-truncated">Older Ledger events are not shown (the newest 1000 are).</p> : null}
      {loadedEmpty ? <p className="tx-muted" data-testid="tx-no-events">No events recorded yet.</p> : null}
      <ol className="tx-rows">
        {view.rows.map((row) => (row.gap ? (
          <li key={row.key} className="tx-row tx-row-gap" data-testid="tx-chain-gap">{row.text}</li>
        ) : (
          <li
            key={row.key}
            className={`tx-row tx-row-${row.source}${row.attention ? ' tx-row-attention' : ''}`}
            data-testid="tx-row"
            data-action={row.ev?.action}
            data-source={row.source}
          >
            <div className="tx-row-head">
              <time dateTime={row.at} title={utcTitle(row.at)}>{row.time}</time>
              <span className="tx-tag" data-testid="tx-row-tag">{row.tag}</span>
              <span className="tx-source" data-testid="tx-row-source">{row.source === 'ledger' ? 'Ledger' : 'Core'}</span>
            </div>
            <p className="tx-sentence" data-testid="tx-row-sentence">{row.sentence.text}</p>
            {row.sentence.fallback ? <p className="tx-muted tx-suffix">{NO_DESCRIPTION_SUFFIX}</p> : null}
            {row.synthesized ? <p className="tx-muted" data-testid="tx-synthesized">(reconstructed from earlier records)</p> : null}
            <p className="tx-row-meta">
              {row.who ? <span data-testid="tx-row-who">{row.who}</span> : null}
              {row.reason ? <span data-testid="tx-row-reason">{`“${row.reason}”`}</span> : null}
              {row.amount ? <span data-testid="tx-row-amount">{row.amount}</span> : null}
              {row.ledgerEntryId ? <span data-testid="tx-row-entry">{`Ledger entry ${row.ledgerEntryId}`}</span> : null}
            </p>
            {row.overridden.length ? (
              <p className="tx-row-override" data-testid="tx-row-override">{`Overrode: ${row.overridden.map(conditionBadge).join(', ')}, with this reason.`}</p>
            ) : null}
            {row.ev?.cause ? (
              <details className="tx-why" open={row.ev.action === 'quoted'}>
                <summary>Why</summary>
                <CauseFacts cause={row.ev.cause} zone={zone} canAmounts={model.canViewAmounts} />
              </details>
            ) : null}
          </li>
        )))}
      </ol>
      {view.skew ? <p className="tx-muted" data-testid="tx-skew-note">Core and the Ledger keep their own clocks, so events from different systems within a few seconds of each other may appear in the wrong order.</p> : null}
      {showLinked ? <LinkedDocuments documents={model.children} canAmounts={model.canViewAmounts} zone={zone} renderTxChip={renderTxChip} /> : null}
    </section>
  );
}
